import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { Divider, Grid } from "@mui/material";

import { useFlags } from "launchdarkly-react-client-sdk";

import { usePermissions } from "components/Hook";
import { isEmptyObject } from "components/Screens/CommonUtils";
import PageLoader from "components/common/PageLoader";
import TabComponent from "components/common/Tabs";
import PageToolBar from "components/Widgets/PageToolBar";
import TopHeader from "components/common/TopHeader";

import { GetCustomerUserandCompanyProfileData } from "store/actions/Settings/CustomerUser&CompanyProfileData";

import UserPermissions from "./UserPermissions";
import UserList from "./UserList";
import GeneralInfo from "./GeneralInfo";
import CompanyInfo from "./CompanyInfo";
import ClockInSettings from "./ClockInSettings";
import ProfilePictureV2 from "components/Screens/SettingV2/Profile/ProfilePictureV2";

import "./Setting.scss";

function CustomerSetting() {
  const dispatch = useDispatch();

  const { platformHeader, teamManagement, clockinCustomerExperience } = useFlags();

  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = React.useState(0);

  const { loggedInUser, loggedInUserType } = usePermissions();

  const userAdditionalInfo = useSelector((state) => state.UserAdditionalInfo.userAdditionalInfo);
  const { clockinExperienceEnabled } = userAdditionalInfo || {};
  const loadingAdditionalInfo = useSelector((state) => state.UserAdditionalInfo.loadingAdditionalInfo);

  React.useEffect(() => {
    const initialSelectedTab = {
      edge: 2,
      customer: 1,
    };
    const initialSelectedTabNew = {
      edge: 1,
      customer: 0,
    };

    const tabParam = searchParams.get("tab");
    if (
      tabParam === "clockin" &&
      clockinCustomerExperience &&
      clockinExperienceEnabled &&
      loggedInUserType === "customer"
    ) {
      setTab(teamManagement ? 2 : 3);
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: true });
    } else {
      setTab(teamManagement ? initialSelectedTabNew[loggedInUserType] : initialSelectedTab[loggedInUserType]);
    }

    if (isEmptyObject(loggedInUser)) {
      const getProfileDataRequest = {
        customer: `customerUser/${loggedInUser?.userId}`,
        edge: `user/${loggedInUser?.userId}`,
      };

      dispatch(
        GetCustomerUserandCompanyProfileData({
          request: getProfileDataRequest[loggedInUserType],
        })
      );
    }
  }, [loggedInUser, loggedInUserType]);

  const getTabs = () => {
    if (loggedInUserType === "edge") {
      return [{ label: "Manage Users" }];
    }
    const tabs = [
      { label: "Company Information" },
      {
        label: "User Management",
      },
    ];
    if (clockinCustomerExperience && clockinExperienceEnabled) {
      tabs.push({ label: "Attendance Management" });
    }
    return tabs;
  };

  return (
    <>
      {loadingAdditionalInfo && <PageLoader />}
      <Grid className="account-settings-container">
        <TopHeader
          title="Account settings"
          linkName="Account"
          breadcrumbs={!platformHeader}
          backIcon={platformHeader ? "" : "/main/dashboard"}
        />

        <div className="settings-page-container">
          <Grid
            container
            rowSpacing={3}
            className={`settings-tab-header ${platformHeader ? "with-platform-header" : "without-platform-header"}`}
          >
            {teamManagement ? (
              <div className="tab-container">
                <TabComponent tabs={getTabs()} tab={tab} setTab={setTab} />
              </div>
            ) : (
              <PageToolBar
                tab1Title={{
                  title: loggedInUserType !== "edge" ? "Company Information" : null,
                  navLink: null,
                }}
                tab2Title={{
                  title: loggedInUserType !== "edge" ? "User Management" : "Manage Users",
                  navLink: null,
                }}
                tab={tab}
                setTab={setTab}
              />
            )}
          </Grid>
          <Grid>
            {(teamManagement ? tab === 0 : tab === 1) && (
              <div className="company-profile-section">
                <div className="general-company-info">
                  <div className="general-info-wrapper">
                    <GeneralInfo title="General Information" />
                  </div>

                  <Divider orientation="horizontal" />

                  <div className="company-info-wrapper">
                    <CompanyInfo title="Company Information" />
                  </div>
                </div>
                <div className="profile-picture-wrapper">
                  <ProfilePictureV2 page="companyProfile" />
                </div>
              </div>
            )}
            {(teamManagement ? tab === 1 : tab === 2) && (
              <Grid className="user-list-tab">{teamManagement ? <UserList /> : <UserPermissions />}</Grid>
            )}
            {clockinCustomerExperience &&
              clockinExperienceEnabled &&
              loggedInUserType === "customer" &&
              (teamManagement ? tab === 2 : tab === 3) && (
                <Grid className="user-list-tab">
                  <ClockInSettings />
                </Grid>
              )}
          </Grid>
        </div>
      </Grid>
    </>
  );
}

export default CustomerSetting;
